// Handle Twitch blocked-terms sync in the library censorship tools

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import type { UserAuthFields } from '@airhead-games/shared/types';
import { ApiFetchError } from '../../../transport/api/baseFetch';
import {
  initiateTwitchModScope,
  syncBlockedTerms,
} from '../../../transport/api/protectedApi';
import type { LibraryDialog } from '../libraryRouteTypes';

export type LibraryBlockedTermsSyncStatus =
  'idle' | 'syncing' | 'synced' | 'error';

export type LibraryBlockedTermsSyncResult = Exclude<
  LibraryBlockedTermsSyncStatus,
  'syncing'
>;

type UseLibraryBlockedTermsSyncParams = {
  user: UserAuthFields | null;
  hasModScopeGrant: boolean;
  dialog: LibraryDialog | null;
  setUser: (user: UserAuthFields | null) => void;
  openDialog: (dialog: LibraryDialog) => void;
};

const MOD_SCOPE_REDIRECT_PATH =
  '/library?section=censorship&dialog=blocked-twitch-terms';

const isScopeRequiredBody = (body: unknown) =>
  typeof body === 'object' &&
  body !== null &&
  'error' in body &&
  (body as { error?: unknown }).error === 'scope_required';

export function useLibraryBlockedTermsSync({
  user,
  hasModScopeGrant,
  dialog,
  setUser,
  openDialog,
}: UseLibraryBlockedTermsSyncParams) {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledModScopeGrantRef = useRef(false);
  const inFlightSyncRef = useRef<Promise<LibraryBlockedTermsSyncResult> | null>(
    null
  );
  const [syncStatus, setSyncStatus] =
    useState<LibraryBlockedTermsSyncStatus>('idle');

  const requestTwitchModScope = useCallback(() => {
    initiateTwitchModScope({
      redirectTo: MOD_SCOPE_REDIRECT_PATH,
    });
  }, []);

  const resetBlockedTermsSyncStatus = useCallback(() => {
    setSyncStatus('idle');
  }, []);

  const handleSyncBlockedTerms =
    useCallback((): Promise<LibraryBlockedTermsSyncResult> => {
      if (!user) {
        openDialog('login');
        return Promise.resolve('idle');
      }

      if (inFlightSyncRef.current) {
        return inFlightSyncRef.current;
      }

      const run = async (): Promise<LibraryBlockedTermsSyncResult> => {
        setSyncStatus('syncing');

        try {
          const response = await syncBlockedTerms();

          if (response.data?.success && response.data.user) {
            setUser(response.data.user);
            setSyncStatus('synced');
            return 'synced';
          }

          if (response.data?.error === 'scope_required') {
            requestTwitchModScope();
            return 'idle';
          }

          throw new Error('Blocked terms sync failed.');
        } catch (error) {
          if (
            error instanceof ApiFetchError &&
            isScopeRequiredBody(error.response)
          ) {
            requestTwitchModScope();
            return 'idle';
          }

          setSyncStatus('error');
          toast('Twitch sync failed. Try again.');
          return 'error';
        }
      };

      const promise = run().finally(() => {
        inFlightSyncRef.current = null;
      });
      inFlightSyncRef.current = promise;
      return promise;
    }, [openDialog, requestTwitchModScope, setUser, user]);

  useEffect(() => {
    if (
      !user?.authTwitch?.userId ||
      !hasModScopeGrant ||
      dialog !== 'blocked-twitch-terms' ||
      handledModScopeGrantRef.current
    ) {
      return;
    }

    handledModScopeGrantRef.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('modScopeGranted');
    setSearchParams(nextParams, { replace: true });
    void handleSyncBlockedTerms();
  }, [
    dialog,
    handleSyncBlockedTerms,
    hasModScopeGrant,
    searchParams,
    setSearchParams,
    user,
  ]);

  return {
    syncStatus,
    handleSyncBlockedTerms,
    requestTwitchModScope,
    resetBlockedTermsSyncStatus,
  };
}
